import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import {
  useCarbonProjectsSelector,
  useFavoriteProjectsSelector,
  useForestationProjectsSelector,
  usePaginationSelector,
  useRecommendedProjectsSelector,
  useSearchSelector,
  useSelectedProjectSelector,
  useSelectedRecommendationTypeSelector,
  useMangroveViewModeSelector,
} from '../../../store/projects/selectors';
import { loadProjectsSpecific } from '../../../store/projects/actions';
import { fetchFilters } from '../store-utils';

/**
 * Custom hook that encapsulates all data fetching and state management logic for the HomePage
 */
export const useHomePageData = () => {
  const dispatch = useDispatch();
  const { page, perPage } = usePaginationSelector();
  const search = useSearchSelector();
  const selectedProject = useSelectedProjectSelector();
  const selectedRecommendation = useSelectedRecommendationTypeSelector();
  const mangroveViewMode = useMangroveViewModeSelector();

  const forestationData = useForestationProjectsSelector();
  const carbonData = useCarbonProjectsSelector();
  const recommendedData = useRecommendedProjectsSelector();
  const favoriteData = useFavoriteProjectsSelector();

  const debouncedLoadProjects = debounce(() => {
    dispatch(loadProjectsSpecific.submit());
  }, 200);

  const initializeData = async () => {
    fetchFilters();
    debouncedLoadProjects();
  };

  // Initial data load
  useEffect(() => {
    initializeData();
  }, []);

  // Reload data when dependencies change
  useEffect(() => {
    debouncedLoadProjects();

    return () => {
      debouncedLoadProjects.cancel();
    };
  }, [search, selectedProject, selectedRecommendation, page, perPage, mangroveViewMode]);

  const calculateTotalCount = () => {
    const {
      data: { total: recommendedTotals },
    } = recommendedData;
    const {
      data: { total: carbonTotals },
    } = carbonData;

    return selectedProject === 'recommendation'
      ? recommendedTotals
      : selectedProject === 'current'
        ? carbonTotals
        : carbonTotals;
  };

  const result = {
    // Data
    forestationProjects: {
      items: forestationData.data.items,
      total: forestationData.data.total,
      isLoading: forestationData.isLoading,
    },
    carbonProjects: {
      items: carbonData.data.items,
      total: carbonData.data.total,
      isLoading: carbonData.isLoading,
      error: carbonData.error || null,
    },
    recommendedProjects: {
      items: recommendedData.data.items as any[], // Temporary fix for type mismatch
      total: recommendedData.data.total,
      pagination: recommendedData.data.pagination,
      isLoading: recommendedData.isLoading,
      error: recommendedData.error || null,
    },
    favoriteProjects: {
      items: favoriteData.data.items,
    },
    // State
    pagination: { page, perPage },
    search,
    selectedProject,
    selectedRecommendation,
    totalCounts: calculateTotalCount(),
  };

  return result;
};
